import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaFileAlt, FaPaperPlane, FaSync, FaUpload, FaEye, FaTrash, FaCheck, FaTimes, FaCogs } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/PreviredPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de Previred
 * Generación y gestión de declaraciones mensuales
 * Carga y auditoría de PDFs de certificados Previred
 */
const PreviredPage = ({ token, id_company }) => {
    const [previred, setPrevired] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros
    const currentDate = new Date();
    const [filtroMes, setFiltroMes] = useState(currentDate.getMonth() + 1);
    const [filtroAnio, setFiltroAnio] = useState(currentDate.getFullYear());

    // Estados para carga de PDF
    const [pdfFile, setPdfFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [auditoria, setAuditoria] = useState([]);
    const [loadingAuditoria, setLoadingAuditoria] = useState(false);

    // Modal de detalle
    const [modalDetalle, setModalDetalle] = useState(null);
    const [activeTab, setActiveTab] = useState('AFP');

    useEffect(() => {
        fetchPrevired();
        fetchAuditoria();
    }, [filtroMes, filtroAnio]);

    const fetchPrevired = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/previred`, {
                params: { id_company, mes: filtroMes, anio: filtroAnio },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setPrevired(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar Previred:', error);
            Swal.fire('Error', 'No se pudieron cargar los registros de Previred', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditoria = async () => {
        try {
            setLoadingAuditoria(true);
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/previred/auditoria`, {
                params: { id_company, periodo_mes: filtroMes, periodo_anio: filtroAnio },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setAuditoria(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar auditoría:', error);
        } finally {
            setLoadingAuditoria(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else if (file) {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = '';
        }
    };

    const handleUploadPdf = async () => {
        if (!pdfFile) {
            Swal.fire('Error', 'Seleccione un archivo PDF', 'error');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('pdf', pdfFile);
            formData.append('id_company', id_company);
            formData.append('periodo_mes', filtroMes);
            formData.append('periodo_anio', filtroAnio);

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/previred/extract-from-pdf`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                Swal.fire({
                    title: 'PDF Procesado',
                    html: `
                        <p><strong>AFPs encontradas:</strong> ${response.data.data.afps_encontradas?.join(', ') || 'N/A'}</p>
                        <p><strong>Trabajadores:</strong> ${response.data.data.resumen?.total_trabajadores || 0}</p>
                        <p><strong>Total Remuneraciones:</strong> $${parseInt(response.data.data.resumen?.total_remuneraciones || 0).toLocaleString('es-CL')}</p>
                        <p><strong>Registros insertados:</strong> ${response.data.data.detalles_insertados || 0}</p>
                    `,
                    icon: 'success'
                });
                setPdfFile(null);
                document.getElementById('pdf-input').value = '';
                fetchAuditoria();
            }
        } catch (error) {
            console.error('Error al procesar PDF:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al procesar el PDF', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleVerDetalle = async (id) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/previred/auditoria/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setModalDetalle(response.data.data);
                setActiveTab('AFP');
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
            Swal.fire('Error', 'No se pudo cargar el detalle', 'error');
        }
    };

    const handleEliminarAuditoria = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar registro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(
                    `${API_BASE_URL}/api/rrhh/previred/auditoria/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
                fetchAuditoria();
            } catch (error) {
                console.error('Error al eliminar:', error);
                Swal.fire('Error', 'No se pudo eliminar el registro', 'error');
            }
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await axios.patch(
                `${API_BASE_URL}/api/rrhh/previred/auditoria/${id}/estado`,
                { estado: nuevoEstado },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire('Éxito', `Estado cambiado a ${nuevoEstado}`, 'success');
            fetchAuditoria();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
        }
    };

    const handleReprocesar = async (id, periodo) => {
        // Calcular mes y año siguiente para mostrar las fechas de pago
        const [mesStr, anioStr] = periodo.split('/');
        const mes = parseInt(mesStr);
        const anio = parseInt(anioStr);
        let mesSiguiente = mes + 1;
        let anioSiguiente = anio;
        if (mesSiguiente > 12) {
            mesSiguiente = 1;
            anioSiguiente = anio + 1;
        }
        const mesesNombres = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        const result = await Swal.fire({
            title: 'Procesar Asientos Contables',
            html: `
                <p>Se ejecutará el proceso de:</p>
                <ul style="text-align: left; margin-left: 20px;">
                    <li>Crear/actualizar liquidaciones de sueldo</li>
                    <li><strong>Devengado:</strong> ${mesesNombres[mes]} ${anio} (día 1)</li>
                    <li><strong>Pago Remuneraciones:</strong> 5 de ${mesesNombres[mesSiguiente]} ${anioSiguiente}</li>
                    <li><strong>Pago Previred:</strong> 13 de ${mesesNombres[mesSiguiente]} ${anioSiguiente}</li>
                </ul>
                <p><strong>Período de devengo:</strong> ${periodo}</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, procesar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2bc046'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Procesando...',
                    html: 'Generando liquidaciones y asientos contables',
                    allowOutsideClick: false,
                    didOpen: () => Swal.showLoading()
                });

                const response = await axios.post(
                    `${API_BASE_URL}/api/rrhh/previred/auditoria/${id}/reprocesar`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    const prop = response.data.data.propagacion?.resultados || {};
                    const pagoRem = response.data.data.propagacion?.asientosPagoRemuneraciones || {};
                    const pagoPrev = response.data.data.propagacion?.asientosPagoPrevired || {};
                    Swal.fire({
                        title: 'Procesamiento Completado',
                        html: `
                            <div style="text-align: left;">
                                <p><strong>Trabajadores procesados:</strong> ${prop.procesados || 0}</p>
                                ${prop.colaboradoresCreados > 0 ?
                                    `<p style="color: #2bc046;"><strong>Colaboradores creados:</strong> ${prop.colaboradoresCreados}</p>` : ''}
                                <p><strong>Liquidaciones creadas:</strong> ${prop.liquidacionesCreadas || 0}</p>
                                <p><strong>Liquidaciones actualizadas:</strong> ${prop.liquidacionesActualizadas || 0}</p>
                                <hr style="margin: 10px 0;">
                                <p><strong>Asientos de devengado:</strong> ${prop.asientosDevengadoGenerados || 0}</p>
                                ${pagoRem.totalLiquidoAPagar > 0 ?
                                    `<p><strong>Pago remuneraciones:</strong> $${parseInt(pagoRem.totalLiquidoAPagar).toLocaleString('es-CL')} (${pagoRem.fechaPago || 'día 5 mes sig.'})</p>` : ''}
                                ${pagoPrev.totales?.totalPago > 0 ?
                                    `<p><strong>Pago Previred:</strong> $${parseInt(pagoPrev.totales.totalPago).toLocaleString('es-CL')} (${pagoPrev.fechaPago || 'día 13 mes sig.'})</p>` : ''}
                                ${prop.trabajadoresNoEncontrados?.length > 0 ?
                                    `<p style="color: orange;"><strong>No procesados:</strong> ${prop.trabajadoresNoEncontrados.map(t => t.rut).join(', ')}</p>` : ''}
                                ${prop.errores?.length > 0 ?
                                    `<p style="color: red;"><strong>Errores:</strong> ${prop.errores.length}</p>` : ''}
                            </div>
                        `,
                        icon: 'success'
                    });
                    fetchAuditoria();
                }
            } catch (error) {
                console.error('Error al reprocesar:', error);
                Swal.fire('Error', error.response?.data?.message || 'Error al procesar', 'error');
            }
        }
    };

    const handleGenerar = async () => {
        try {
            const result = await Swal.fire({
                title: 'Generar Previred',
                text: `¿Desea generar la declaración de Previred para ${meses[filtroMes - 1]} ${filtroAnio}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, generar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.post(
                    `${API_BASE_URL}/api/rrhh/previred/generar`,
                    { id_company, mes: filtroMes, anio: filtroAnio },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Previred generado correctamente', 'success');
                    fetchPrevired();
                }
            }
        } catch (error) {
            console.error('Error al generar Previred:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al generar Previred', 'error');
        }
    };

    const handleMarcarEnviado = async (id) => {
        try {
            const { value: numeroFolio } = await Swal.fire({
                title: 'Marcar como Enviado',
                input: 'text',
                inputLabel: 'Número de folio Previred',
                inputPlaceholder: 'Ingrese el folio...',
                showCancelButton: true,
                confirmButtonText: 'Marcar',
                cancelButtonText: 'Cancelar'
            });

            if (numeroFolio) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/previred/${id}/enviar`,
                    { numero_folio: numeroFolio },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Previred marcado como enviado', 'success');
                    fetchPrevired();
                }
            }
        } catch (error) {
            console.error('Error al marcar Previred:', error);
            Swal.fire('Error', 'No se pudo marcar como enviado', 'error');
        }
    };

    const meses = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    return (
        <div className="previred-container">
            <div className="previred-header">
                <div>
                    <h1><FaFileAlt /> Previred</h1>
                    <p>Declaraciones mensuales de cotizaciones previsionales</p>
                </div>
                <button className="btn-generar" onClick={handleGenerar}>
                    <FaSync /> Generar Previred
                </button>
            </div>

            {/* Filtros */}
            <div className="previred-filters">
                <div className="filter-group">
                    <label>Mes</label>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
                        {meses.map((mes, index) => (
                            <option key={index} value={index + 1}>{mes}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Año</label>
                    <select value={filtroAnio} onChange={(e) => setFiltroAnio(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Sección de carga de PDF */}
            <div className="upload-section">
                <h2><FaUpload /> Cargar Certificado Previred (PDF)</h2>
                <div className="upload-form">
                    <input
                        type="file"
                        id="pdf-input"
                        accept=".pdf"
                        onChange={handleFileChange}
                        disabled={uploading}
                    />
                    {pdfFile && (
                        <span className="file-name">{pdfFile.name}</span>
                    )}
                    <button
                        className="btn-upload"
                        onClick={handleUploadPdf}
                        disabled={!pdfFile || uploading}
                    >
                        {uploading ? 'Procesando...' : 'Extraer Datos'}
                    </button>
                </div>
            </div>

            {/* Tabla de Auditoría */}
            {auditoria.length > 0 && (
                <div className="auditoria-section">
                    <h2><FaEye /> Registros Cargados (Auditoría)</h2>
                    {loadingAuditoria ? (
                        <div className="loading">Cargando...</div>
                    ) : (
                        <table className="auditoria-table">
                            <thead>
                                <tr>
                                    <th>Archivo</th>
                                    <th>Período</th>
                                    <th>Empleador</th>
                                    <th>AFPs</th>
                                    <th>Trabajadores</th>
                                    <th>Total Rem.</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditoria.map(reg => (
                                    <tr key={reg.id}>
                                        <td>{reg.nombre_archivo}</td>
                                        <td>{reg.periodo_mes}/{reg.periodo_anio}</td>
                                        <td>{reg.empleador_razon_social || '-'}</td>
                                        <td>{reg.total_afps || 0}</td>
                                        <td>{reg.total_trabajadores || 0}</td>
                                        <td>${parseInt(reg.total_remuneraciones || 0).toLocaleString('es-CL')}</td>
                                        <td>
                                            <span className={`badge badge-${reg.estado?.toLowerCase()}`}>
                                                {reg.estado}
                                            </span>
                                        </td>
                                        <td className="actions">
                                            <button
                                                className="btn-icon btn-view"
                                                onClick={() => handleVerDetalle(reg.id)}
                                                title="Ver detalle"
                                            >
                                                <FaEye />
                                            </button>
                                            {reg.estado !== 'PROCESADO' && (
                                                <button
                                                    className="btn-icon btn-process"
                                                    onClick={() => handleReprocesar(reg.id, `${reg.periodo_mes}/${reg.periodo_anio}`)}
                                                    title="Procesar asientos contables"
                                                >
                                                    <FaCogs />
                                                </button>
                                            )}
                                            {reg.estado === 'PENDIENTE' && (
                                                <button
                                                    className="btn-icon btn-success"
                                                    onClick={() => handleCambiarEstado(reg.id, 'VALIDADO')}
                                                    title="Marcar como validado"
                                                >
                                                    <FaCheck />
                                                </button>
                                            )}
                                            <button
                                                className="btn-icon btn-danger"
                                                onClick={() => handleEliminarAuditoria(reg.id)}
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Modal de detalle */}
            {modalDetalle && (
                <div className="modal-overlay" onClick={() => setModalDetalle(null)}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Detalle - {modalDetalle.cabecera?.periodo_mes}/{modalDetalle.cabecera?.periodo_anio}</h2>
                            <button className="btn-close" onClick={() => setModalDetalle(null)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-info">
                            <p><strong>Empleador:</strong> {modalDetalle.cabecera?.empleador_razon_social}</p>
                            <p><strong>RUT:</strong> {modalDetalle.cabecera?.empleador_rut}</p>
                            <p><strong>AFPs:</strong> {modalDetalle.afps?.join(', ')}</p>
                        </div>

                        <div className="modal-tabs">
                            <button
                                className={activeTab === 'AFP' ? 'active' : ''}
                                onClick={() => setActiveTab('AFP')}
                            >
                                AFP ({modalDetalle.detalles?.AFP?.length || 0})
                            </button>
                            <button
                                className={activeTab === 'ISL' ? 'active' : ''}
                                onClick={() => setActiveTab('ISL')}
                            >
                                ISL ({modalDetalle.detalles?.ISL?.length || 0})
                            </button>
                            <button
                                className={activeTab === 'SALUD' ? 'active' : ''}
                                onClick={() => setActiveTab('SALUD')}
                            >
                                SALUD ({modalDetalle.detalles?.SALUD?.length || 0})
                            </button>
                        </div>

                        <div className="modal-body">
                            <table className="detalle-table">
                                <thead>
                                    <tr>
                                        <th>RUT</th>
                                        <th>Nombre</th>
                                        <th>Institución</th>
                                        <th>Remuneración</th>
                                        {activeTab === 'AFP' && <th>Cot. Obligatoria</th>}
                                        {activeTab === 'AFP' && <th>SIS</th>}
                                        {activeTab === 'AFP' && <th>Cesantía Afil.</th>}
                                        {activeTab === 'AFP' && <th>Cesantía Empl.</th>}
                                        {activeTab === 'ISL' && <th>Tasa</th>}
                                        {activeTab === 'ISL' && <th>Cotización ISL</th>}
                                        {activeTab === 'SALUD' && <th>Entidad</th>}
                                        {activeTab === 'SALUD' && <th>Cotización 7%</th>}
                                        <th>Mov.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalDetalle.detalles?.[activeTab]?.map((det, idx) => (
                                        <tr key={idx}>
                                            <td>{det.rut_trabajador}</td>
                                            <td>{det.nombre_trabajador}</td>
                                            <td>{det.nombre_institucion}</td>
                                            <td>${parseInt(det.remuneracion || 0).toLocaleString('es-CL')}</td>
                                            {activeTab === 'AFP' && <td>${parseInt(det.cotizacion_obligatoria || 0).toLocaleString('es-CL')}</td>}
                                            {activeTab === 'AFP' && <td>${parseInt(det.sis || 0).toLocaleString('es-CL')}</td>}
                                            {activeTab === 'AFP' && <td>${parseInt(det.cotizacion_afiliado_cesantia || 0).toLocaleString('es-CL')}</td>}
                                            {activeTab === 'AFP' && <td>${parseInt(det.cotizacion_empleador_cesantia || 0).toLocaleString('es-CL')}</td>}
                                            {activeTab === 'ISL' && <td>{det.tasa_cotizacion_isl}%</td>}
                                            {activeTab === 'ISL' && <td>${parseInt(det.cotizacion_isl || 0).toLocaleString('es-CL')}</td>}
                                            {activeTab === 'SALUD' && <td>{det.entidad_salud}</td>}
                                            {activeTab === 'SALUD' && <td>${parseInt(det.cotizacion_salud_7pct || 0).toLocaleString('es-CL')}</td>}
                                            <td>{det.codigo_movimiento}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setModalDetalle(null)}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tarjetas de resumen */}
            {loading ? (
                <div className="loading">Cargando...</div>
            ) : previred.length === 0 ? (
                <div className="empty-state">
                    <FaFileAlt />
                    <h3>No hay declaraciones para este período</h3>
                    <p>Haz clic en "Generar Previred" para crear una nueva declaración</p>
                </div>
            ) : (
                <div className="previred-cards">
                    {previred.map((prev) => (
                        <div key={prev.id} className="previred-card">
                            <div className="card-header">
                                <h3>{meses[prev.mes - 1]} {prev.anio}</h3>
                                <span className={`badge badge-${prev.estado?.toLowerCase() || 'pendiente'}`}>
                                    {prev.estado || 'PENDIENTE'}
                                </span>
                            </div>

                            <div className="card-body">
                                <div className="stat-row">
                                    <span className="stat-label">Total Trabajadores:</span>
                                    <span className="stat-value">{prev.total_trabajadores || 0}</span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">Total Remuneraciones:</span>
                                    <span className="stat-value">${parseInt(prev.total_remuneraciones || 0).toLocaleString('es-CL')}</span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">Total AFP:</span>
                                    <span className="stat-value">${parseInt(prev.total_afp || 0).toLocaleString('es-CL')}</span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">Total Salud:</span>
                                    <span className="stat-value">${parseInt(prev.total_salud || 0).toLocaleString('es-CL')}</span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">Total Cesantía:</span>
                                    <span className="stat-value">${parseInt(prev.total_cesantia || 0).toLocaleString('es-CL')}</span>
                                </div>

                                {prev.numero_folio && (
                                    <div className="stat-row">
                                        <span className="stat-label">Folio:</span>
                                        <span className="stat-value">{prev.numero_folio}</span>
                                    </div>
                                )}

                                {prev.fecha_envio && (
                                    <div className="stat-row">
                                        <span className="stat-label">Fecha Envío:</span>
                                        <span className="stat-value">
                                            {new Date(prev.fecha_envio).toLocaleDateString('es-CL')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="card-actions">
                                {prev.estado === 'PENDIENTE' && (
                                    <button
                                        className="btn-enviar"
                                        onClick={() => handleMarcarEnviado(prev.id)}
                                    >
                                        <FaPaperPlane /> Marcar como Enviado
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PreviredPage;
